import type { ElementType, ComponentPropsWithoutRef } from 'react';
import clsx from 'clsx';

import { usePaginationContext } from './Pagination.context';
import { PaginationControl } from './PaginationControl';

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
      clipRule="evenodd"
    />
  </svg>
);

const ChevronDoubleRightIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M10.21 14.77a.75.75 0 0 1 .02-1.06L14.168 10 10.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
      clipRule="evenodd"
    />
    <path
      fillRule="evenodd"
      d="M4.21 14.77a.75.75 0 0 1 .02-1.06L8.168 10 4.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
      clipRule="evenodd"
    />
  </svg>
);

const ChevronDoubleLeftIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M15.79 14.77a.75.75 0 0 1-1.06.02l-4.5-4.25a.75.75 0 0 1 0-1.08l4.5-4.25a.75.75 0 1 1 1.04 1.08L11.832 10l3.938 3.71a.75.75 0 0 1 .02 1.06Z"
      clipRule="evenodd"
    />
    <path
      fillRule="evenodd"
      d="M9.79 14.77a.75.75 0 0 1-1.06.02l-4.5-4.25a.75.75 0 0 1 0-1.08l4.5-4.25a.75.75 0 1 1 1.04 1.08L5.832 10l3.938 3.71a.75.75 0 0 1 .02 1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

type EdgeComponentProps = ComponentPropsWithoutRef<typeof PaginationControl> & {
  Icon?: ElementType;
};

function createEdgeComponent({
  icon,
  type,
  action,
}: {
  icon: ElementType;
  type: 'next' | 'previous';
  action: 'onNext' | 'onPrevious' | 'onFirst' | 'onLast';
}) {
  function EdgeComponent({ Icon = icon, ...props }: EdgeComponentProps) {
    const ctx = usePaginationContext();
    const disabled = type === 'next' ? ctx.active === ctx.total : ctx.active === 1;

    return (
      <PaginationControl
        disabled={ctx.disabled || disabled}
        onClick={ctx[action]}
        className={clsx('pagination-control-icon', ctx.classNames?.controlIcon)}
        {...props}
      >
        <Icon className={clsx('pagination-icon rtl:rotate-180', ctx.classNames?.icon)} />
      </PaginationControl>
    );
  }

  EdgeComponent.displayName = 'Pagination' + type.charAt(0).toUpperCase() + type.slice(1);

  return EdgeComponent;
}

export const PaginationNext = createEdgeComponent({
  icon: ChevronRightIcon,
  type: 'next',
  action: 'onNext',
});

export const PaginationPrevious = createEdgeComponent({
  icon: ChevronLeftIcon,
  type: 'previous',
  action: 'onPrevious',
});

export const PaginationFirst = createEdgeComponent({
  icon: ChevronDoubleLeftIcon,
  type: 'previous',
  action: 'onFirst',
});

export const PaginationLast = createEdgeComponent({
  icon: ChevronDoubleRightIcon,
  type: 'next',
  action: 'onLast',
});
